import type {
  CreateTenantMemberDto,
  CreateTenantMemberResponse,
  MemberPaymentsAnnotation,
  MemberRolesResponse,
  RegenerateInvitationResponse,
  UpdateAthleteDto,
  UpdateMemberRolesDto,
} from '../../api/tenant-members.service';
import type { TenantAccountRole } from '../../auth/tenant-auth.service';
import {
  memberFullName,
  memberMatchesKind,
  toMemberResponse,
  type DemoMember,
  type DemoStoreData,
  type MemberKindFilter,
} from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

function newId(prefix: string, data: DemoStoreData): string {
  return `${prefix}-${data.members.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

function paymentsFor(memberId: string, data: DemoStoreData): MemberPaymentsAnnotation | null {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const payment = data.payments.find(
    (p) => p.studentTenantAccountId === memberId && p.year === year && p.month === month,
  );
  if (!payment) return null;
  const overdueMonths = data.payments.filter(
    (p) =>
      p.studentTenantAccountId === memberId &&
      p.status === 'Unpaid' &&
      (p.year < year || (p.year === year && p.month < month)),
  ).length;
  return {
    currentMonthStatus:
      payment.amountDue === 0 ? 'NoFee' : payment.status,
    currentMonthAmountDue: payment.amountDue,
    currentMonthAmountPaid: payment.amountPaid ?? 0,
    isLate: payment.status === 'Unpaid' && overdueMonths > 0,
    overdueMonths,
  };
}

function toRolesResponse(m: DemoMember): MemberRolesResponse {
  return {
    id: m.id,
    email: m.email,
    firstName: m.firstName,
    lastName: m.lastName,
    fullName: memberFullName(m),
    phone: m.phone,
    roles: m.roles,
    status: m.status,
    isClaimed: m.isClaimed,
    pending: m.pending,
    createdAt: m.createdAt,
    invitationExpiresAt: null,
    title: null,
    weightKg: m.weightKg,
    heightCm: m.heightCm,
    birthdate: m.birthdate,
    gender: m.gender,
    profilePictureUrl: m.profilePictureUrl,
    karateBeltId: null,
  };
}

export const membersHandlers: readonly HandlerEntry[] = [
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/(coaches|athletes)$/,
    (ctx, data) => {
      const kind = ctx.match[2] as MemberKindFilter;
      const includePayments = ctx.query.has('includePaymentsAsOf');
      return data.members
        .filter((m) => memberMatchesKind(m, kind))
        .map((m) =>
          toMemberResponse(
            m,
            kind === 'athletes' && includePayments ? paymentsFor(m.id, data) : null,
          ),
        );
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/(coaches|athletes)$/,
    (ctx) => {
      const kind = ctx.match[2] as MemberKindFilter;
      const body = ctx.body as CreateTenantMemberDto;
      return {
        mutate: (data: DemoStoreData) => {
          const id = newId(kind === 'athletes' ? 'athlete' : 'coach', data);
          const role: TenantAccountRole = kind === 'athletes' ? 'Student' : 'Coach';
          const member: DemoMember = {
            id,
            email: body.email ?? null,
            firstName: body.firstName ?? '',
            lastName: body.lastName,
            phone: body.phone ?? null,
            roles: [role],
            status: 'Active',
            isClaimed: false,
            pending: true,
            createdAt: new Date().toISOString(),
            weightKg: null,
            heightCm: null,
            birthdate: null,
            gender: null,
            profilePictureUrl: null,
          };
          const expiresAt = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString();
          const result: CreateTenantMemberResponse = {
            member: toMemberResponse(member, null),
            invitationUrl: '#',
            invitationExpiresAt: expiresAt,
            claimUrl: '#',
            claimExpiresAt: expiresAt,
          };
          return {
            next: { ...data, members: [...data.members, member] },
            result,
          };
        },
      };
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/(coaches|athletes)\/([^/]+)\/invitation$/,
    () => {
      const expiresAt = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString();
      const result: RegenerateInvitationResponse = {
        invitationUrl: '#',
        expiresAt,
        claimUrl: '#',
      };
      return result;
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/athletes\/([^/]+)\/claim-token$/,
    () => {
      const expiresAt = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString();
      const result: RegenerateInvitationResponse = {
        invitationUrl: '#',
        expiresAt,
        claimUrl: '#',
      };
      return result;
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/athletes\/([^/]+)$/,
    (ctx, data) => {
      const id = decodeURIComponent(ctx.match[2]);
      const member = data.members.find((m) => m.id === id);
      if (!member) return { error: { status: 404, message: 'Not found' } };
      return toMemberResponse(member, paymentsFor(id, data));
    },
  ),
  route(
    'PATCH',
    /^\/api\/tenants\/([^/]+)\/athletes\/([^/]+)$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as UpdateAthleteDto;
      return {
        mutate: (data: DemoStoreData) => {
          const members = data.members.map((m) =>
            m.id === id
              ? {
                  ...m,
                  firstName: body.firstName === undefined ? m.firstName : body.firstName,
                  lastName: body.lastName === undefined ? m.lastName : body.lastName,
                  phone: body.phone === undefined ? m.phone : body.phone,
                  weightKg: body.weightKg === undefined ? m.weightKg : body.weightKg,
                  heightCm: body.heightCm === undefined ? m.heightCm : body.heightCm,
                  birthdate: body.birthdate === undefined ? m.birthdate : body.birthdate,
                }
              : m,
          );
          const member = members.find((m) => m.id === id)!;
          return {
            next: { ...data, members },
            result: toMemberResponse(member, paymentsFor(id, data)),
          };
        },
      };
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/athletes\/([^/]+)\/status$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as { status: 'Active' | 'Inactive' };
      return {
        mutate: (data: DemoStoreData) => {
          const members = data.members.map((m) =>
            m.id === id ? { ...m, status: body.status } : m,
          );
          const member = members.find((m) => m.id === id)!;
          return {
            next: { ...data, members },
            result: toMemberResponse(member, paymentsFor(id, data)),
          };
        },
      };
    },
  ),
  route(
    'DELETE',
    /^\/api\/tenants\/([^/]+)\/(coaches|athletes)\/([^/]+)$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[3]);
      return {
        mutate: (data: DemoStoreData) => ({
          next: {
            ...data,
            members: data.members.map((m) =>
              m.id === id ? { ...m, status: 'Removed' as const } : m,
            ),
          },
          result: undefined as void,
        }),
      };
    },
  ),
  route(
    'PUT',
    /^\/api\/tenants\/([^/]+)\/members\/([^/]+)\/roles$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as UpdateMemberRolesDto;
      return {
        mutate: (data: DemoStoreData) => {
          const members = data.members.map((m) =>
            m.id === id ? { ...m, roles: body.roles } : m,
          );
          const member = members.find((m) => m.id === id)!;
          return { next: { ...data, members }, result: toRolesResponse(member) };
        },
      };
    },
  ),
  route('GET', /^\/api\/tenants\/([^/]+)\/me\/profile$/, (_ctx, data) => {
    const member = data.members.find((m) => m.id === data.currentMemberId);
    if (!member) return { error: { status: 404, message: 'Not found' } };
    return toMemberResponse(member, null);
  }),
  route('PATCH', /^\/api\/tenants\/([^/]+)\/me\/profile$/, (ctx) => {
    const body = ctx.body as UpdateAthleteDto;
    return {
      mutate: (data: DemoStoreData) => {
        const members = data.members.map((m) =>
          m.id === data.currentMemberId
            ? {
                ...m,
                firstName: body.firstName === undefined ? m.firstName : body.firstName,
                lastName: body.lastName === undefined ? m.lastName : body.lastName,
                phone: body.phone === undefined ? m.phone : body.phone,
                weightKg: body.weightKg === undefined ? m.weightKg : body.weightKg,
                heightCm: body.heightCm === undefined ? m.heightCm : body.heightCm,
                birthdate: body.birthdate === undefined ? m.birthdate : body.birthdate,
              }
            : m,
        );
        const member = members.find((m) => m.id === data.currentMemberId)!;
        return { next: { ...data, members }, result: toMemberResponse(member, null) };
      },
    };
  }),
];
